import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedPracticeColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialPracticeColumns: PersistedPracticeColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'partner_name',
    headerName: 'Partner',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'county',
    headerName: 'County',
    minWidth: 140,
    flex: 1,
    sortable: false,
    filterable: false,
  },
  {
    field: 'phone_number',
    headerName: 'Phone',
    minWidth: 150,
    flex: 1,
    sortable: false,
    filterable: false,
  },
];

export const initialPracticePinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type ColumnNames =
  'name' | 'partner_name' | 'phone_number' | 'city' | 'state' | 'county';

export interface IPracticeTableFilters {
  search: string;
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
  state: string;
  partner_id: string;
}

const getDefaultFilters = (): IPracticeTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'name',
  page: 1,
  limit: 10,
  state: '',
  partner_id: '',
});

type TKeyOfFilters = keyof IPracticeTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IPracticeTableFilters,
  appliedFilters: IPracticeTableFilters,
): number => {
  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

type TPracticeTableStore = {
  filters: IPracticeTableFilters;
  setFilters: (newFilters: Partial<IPracticeTableFilters>) => void;
  clearStore: () => void;
  appliedFiltersCount: number;

  columns: PersistedPracticeColumn[];
  setColumns: (columns: PersistedPracticeColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

export const usePracticeTableStore = create<TPracticeTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),
      clearStore: () =>
        set(() => ({
          filters: getDefaultFilters(),
          appliedFiltersCount: 0,
        })),
      appliedFiltersCount: 0,

      columns: initialPracticeColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialPracticePinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialPracticeColumns,
          pinnedColumns: initialPracticePinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'practice-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TPracticeTableStore &
            Pick<IPracticeTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IPracticeTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
        };
      },
    },
  ),
);
